import { useEffect, useState } from "react";
import { LuFilter, LuX } from "react-icons/lu";
import Modal from "../../../common/Modal";
import Button from "../../../common/Buttons";
import TextInput from "../../../common/TextInput";
import SearchableSelectInput from "../../../common/SearchableSelectInput";

const EMPTY_FORM = {
  item: "",
  by: "",
  supplier: "",
  type: "",
  catalog: "",
  manufacturer: "",
  project: "",
  status: "",
  unitSize: ["", ""],
  numUnits: ["", ""],
  unitPrice: ["", ""],
};

const STATUS_OPTIONS = [
  { value: "new", label: "New Request" },
  { value: "approved", label: "Approved Request" },
  { value: "poMade", label: "PO Made" },
  { value: "ordered", label: "Order Sent" },
];

export default function RequestsFilter({
  query,
  onApply,
  onClear,
  suppliers,
  types,
  projects,
  userNames,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (open) {
      setForm(
        Object.keys(query).length > 0
          ? {
              ...EMPTY_FORM,
              ...query,
              unitSize: query.unitSize || ["", ""],
              numUnits: query.numUnits || ["", ""],
              unitPrice: query.unitPrice || ["", ""],
            }
          : EMPTY_FORM,
      );
    }
  }, [open, query]);

  const setField = (field) => (value) =>
    setForm((f) => ({ ...f, [field]: value }));
  const setRange = (field, idx) => (e) =>
    setForm((f) => {
      const next = [...f[field]];
      next[idx] = e.target.value;
      return { ...f, [field]: next };
    });

  const hasActiveQuery = Object.keys(query).length > 0;

  const handleApply = () => {
    onApply({
      item: form.item.trim(),
      by: form.by,
      supplier: form.supplier,
      type: form.type,
      catalog: form.catalog.trim(),
      manufacturer: form.manufacturer.trim(),
      project: form.project,
      status: form.status,
      unitSize: form.unitSize,
      numUnits: form.numUnits,
      unitPrice: form.unitPrice,
    });
    setOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 text-[13px] font-medium text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <LuFilter size={14} />
          <span className="hidden sm:inline">Filter Requests</span>
        </button>
        {hasActiveQuery && (
          <button
            type="button"
            onClick={onClear}
            title="Clear filters"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <LuX size={14} />
          </button>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Filter Requests">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            {/* <label className="mb-1 block text-[12px] font-medium text-slate-600">
              Supplier
            </label> */}
            <SearchableSelectInput
              name="supplier"
              label="Supplier"
              options={suppliers.map((supplier) => ({
                value: supplier,
                label: supplier,
              }))}
              placeholder="Any supplier"
              searchPlaceholder="Search supplier..."
              value={form.supplier}
              onChange={(e) => setField("supplier")(e.target.value)}
              error=""
            />
          </div>

          <div>
            <SearchableSelectInput
              name="type"
              label="Type"
              options={types.map((type) => ({
                value: type,
                label: type,
              }))}
              placeholder="Any type"
              searchPlaceholder="Search type..."
              value={form.type}
              onChange={(e) => setField("type")(e.target.value)}
            />
          </div>

          <TextInput
            label="Item Name"
            value={form.item}
            onChange={(e) => setField("item")(e.target.value)}
            placeholder="Item"
          />

          <div>
            <SearchableSelectInput
              name="by"
              label="Requested By"
              options={userNames.map((user) => ({
                value: user,
                label: user,
              }))}
              placeholder="Anyone"
              searchPlaceholder="Search user..."
              value={form.by}
              onChange={(e) => setField("by")(e.target.value)}
            />
          </div>

          <TextInput
            label="Catalog"
            value={form.catalog}
            onChange={(e) => setField("catalog")(e.target.value)}
            placeholder="Catalog"
          />
          <TextInput
            label="Manufacturer"
            value={form.manufacturer}
            onChange={(e) => setField("manufacturer")(e.target.value)}
            placeholder="Manufacturer"
          />

          <div>
            <SearchableSelectInput
              name="project"
              label="Project"
              options={projects.map((project) => ({
                value: project,
                label: project,
              }))}
              placeholder="Any project"
              searchPlaceholder="Search project..."
              value={form.project}
              onChange={(e) => setField("project")(e.target.value)}
            />
          </div>

          <div>
            <SearchableSelectInput
              name="status"
              label="Status"
              options={STATUS_OPTIONS}
              placeholder="Any status"
              searchPlaceholder="Search status..."
              value={form.status}
              onChange={(e) => setField("status")(e.target.value)}
            />
          </div>

          {[
            ["unitSize", "Unit Size"],
            ["numUnits", "Number of Units"],
            ["unitPrice", "Unit Price (₹)"],
          ].map(([field, label]) => (
            <div key={field} className="sm:col-span-2">
              <label className="mb-1 block text-[12px] font-medium text-slate-600">
                {label}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  placeholder="Min"
                  value={form[field][0]}
                  onChange={setRange(field, 0)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                />
                <span className="text-slate-400">–</span>
                <input
                  type="number"
                  min={0}
                  placeholder="Max"
                  value={form[field][1]}
                  onChange={setRange(field, 1)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="solid" onClick={handleApply}>
            Apply Filters
          </Button>
        </div>
      </Modal>
    </>
  );
}
